import { getApiUrl } from '@/shared/api/get-api-url';

export class ApiError extends Error {
    constructor(
        public status: number,
        message: string,
        public response?: unknown,
    ) {
        super(message);
        this.name = 'ApiError';
    }
}

// 403 is the common lapse (cookie maxAge outlives the session window), 401 only when the cookie
// is absent — both mean "not signed in" while every route stays public; revisit once one doesn't
const AUTH_ERROR_STATUSES = [401, 403];

export function isAuthError(error: unknown): boolean {
    return error instanceof ApiError && AUTH_ERROR_STATUSES.includes(error.status);
}

// Without this, `/sign-in`'s own inverse guard would 401, redirect here, and loop forever.
function isOnSignInPage(): boolean {
    return window.location.pathname === '/sign-in';
}

// A full navigation, not a router redirect — this runs outside router context, and the current
// page is the correct returnUrl since the call that fails always runs on it.
function redirectToSignIn(): void {
    const returnUrl = encodeURIComponent(window.location.href);
    window.location.href = `/sign-in?returnUrl=${returnUrl}`;
}

type RequestOptions = {
    signal?: AbortSignal;
    // `/auth/me` so the root guard stays the one authority; sign-out so its own 401 branch
    // (redirect to Cognito's logout) isn't raced by this one.
    skipAuthRedirect?: boolean;
};

type RequestConfig = RequestOptions & {
    method?: string;
    headers?: HeadersInit;
    body?: unknown;
};

class ApiClient {
    private async request<T>(endpoint: string, config: RequestConfig = {}): Promise<T> {
        const { method = 'GET', headers = {}, body, signal, skipAuthRedirect } = config;

        const url = getApiUrl(endpoint);

        const response = await fetch(url, {
            method,
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                ...headers,
            },
            body: body ? JSON.stringify(body) : undefined,
            signal,
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));

            if (
                AUTH_ERROR_STATUSES.includes(response.status) &&
                !skipAuthRedirect &&
                !isOnSignInPage()
            ) {
                redirectToSignIn();
                return new Promise<T>(() => {});
            }

            throw new ApiError(
                response.status,
                errorData.message || `HTTP ${response.status}: ${response.statusText}`,
                errorData,
            );
        }

        if (response.status === 204) {
            return undefined as T;
        }

        return response.json();
    }

    async get<T>(endpoint: string, options?: RequestOptions): Promise<T> {
        return this.request<T>(endpoint, { method: 'GET', ...options });
    }

    async post<T>(endpoint: string, data?: unknown, options?: RequestOptions): Promise<T> {
        return this.request<T>(endpoint, { method: 'POST', body: data, ...options });
    }
}

export const apiClient = new ApiClient();
